import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { getAttributionFields } from "../lib/attribution";

const HS_PORTAL_ID = "342523689";
const HS_FORM_GUID = "2fd30835-db60-4630-b3d9-2b02f8e0f1c5";
const HS_SUBMIT_URL = `https://api.hsforms.com/submissions/v3/integration/submit/${HS_PORTAL_ID}/${HS_FORM_GUID}`;

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
};

export const LeadForm = ({
  showCompanyFields = false,
  phoneRequired = false,
  intentRequired = false,
  title = "Get your free quote",
  subtitle = "Takes 60 seconds. No obligation, ever.",
  intentOptions = [
    { value: "Life insurance for my family", label: "Life insurance for my family" },
    { value: "Life insurance for my business", label: "Life insurance for my business" },
    { value: "Group benefits for my team", label: "Group benefits for my team" },
    { value: "TFSA or investment planning", label: "TFSA or investment planning" },
    { value: "Full financial plan", label: "Full financial plan" },
    { value: "Not sure — I'd like guidance", label: "Not sure — I'd like guidance" },
  ]
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    intent: "",
    companyName: "",
    employeeCount: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitFailed, setSubmitFailed] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError("");
    setSubmitFailed(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.email.trim()) {
      setError("Please fill in your name and email.");
      return;
    }
    if (!showCompanyFields && !formData.intent) {
      setError("Please select what you're looking for.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (phoneRequired) {
      const phoneDigits = formData.phone.replace(/\D/g, "");
      const validPhone = phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.charAt(0) === "1");
      if (!validPhone) {
        setError("Please enter your phone number");
        return;
      }
    }

    setIsSubmitting(true);
    setError("");

    // Build a readable message with the visitor's intent / company context + page.
    const messageParts = [];
    if (showCompanyFields) {
      if (formData.companyName) messageParts.push(`Company: ${formData.companyName}`);
      if (formData.employeeCount) messageParts.push(`Number of employees: ${formData.employeeCount}`);
    } else if (formData.intent) {
      messageParts.push(`Looking for: ${formData.intent}`);
    }
    messageParts.push(`Submitted from: ${window.location.href}`);

    const fields = [
      { objectTypeId: "0-1", name: "firstname", value: formData.firstName },
      { objectTypeId: "0-1", name: "lastname", value: formData.lastName },
      { objectTypeId: "0-1", name: "email", value: formData.email },
      { objectTypeId: "0-1", name: "phone", value: formData.phone },
      { objectTypeId: "0-1", name: "message", value: messageParts.join("\n") },
    ];
    if (!showCompanyFields && formData.intent) {
      fields.push({ objectTypeId: "0-1", name: "what_are_you_looking_for", value: formData.intent });
    }
    fields.push({ objectTypeId: "0-1", name: "lead_source_page", value: window.location.origin + window.location.pathname });
    fields.push(...getAttributionFields());

    const context = { pageUri: window.location.href, pageName: document.title };
    const hutk = getCookie("hubspotutk");
    if (hutk) context.hutk = hutk;

    try {
      const response = await fetch(HS_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fields, context })
      });

      if (response.ok) {
        navigate('/thank-you');
      } else {
        setError("Something went wrong sending your request.");
        setSubmitFailed(true);
      }
    } catch {
      setError("Network error — your request didn't go through.");
      setSubmitFailed(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full px-3.5 py-3 border-[1.5px] border-[#DDD8CC] rounded text-sm text-[#1A2540] bg-white focus:border-[#C9A84C] focus:outline-none transition-colors";
  const labelClass = "block text-[11px] font-medium tracking-wider text-[#6B7A99] uppercase mb-1.5";

  return (
    <form onSubmit={handleSubmit} autoComplete="off" noValidate data-testid="lead-form">
      {title && (
        <div className="font-playfair text-xl font-semibold text-[#0C1B33] mb-1">
          {title}
        </div>
      )}
      {subtitle && (
        <div className="text-sm text-[#6B7A99] mb-6">
          {subtitle}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className={labelClass}>First name</label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Chris"
            className={inputClass}
            data-testid="input-firstname"
          />
        </div>
        <div>
          <label className={labelClass}>Last name</label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Johnstone"
            className={inputClass}
            data-testid="input-lastname"
          />
        </div>
      </div>

      <div className="mb-3">
        <label className={labelClass}>Email address</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="you@example.com"
          className={inputClass}
          data-testid="input-email"
        />
      </div>

      <div className="mb-3">
        <label className={labelClass}>Phone number{phoneRequired ? " *" : ""}</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="(250) 555-0000"
          className={inputClass}
          required={phoneRequired}
          data-testid="input-phone"
        />
      </div>

      {showCompanyFields ? (
        <>
          <div className="mb-3">
            <label className={labelClass}>Company name</label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Your company"
              className={inputClass}
              data-testid="input-company"
            />
          </div>
          <div className="mb-3">
            <label className={labelClass}>Number of employees</label>
            <select
              name="employeeCount"
              value={formData.employeeCount}
              onChange={handleChange}
              className={`${inputClass} appearance-none pr-10`}
              data-testid="select-employees"
            >
              <option value="">Select...</option>
              <option value="1-5">1–5 employees</option>
              <option value="6-15">6–15 employees</option>
              <option value="16-50">16–50 employees</option>
              <option value="51-200">51–200 employees</option>
              <option value="200+">200+ employees</option>
            </select>
          </div>
        </>
      ) : (
        <div className="mb-3">
          <label className={labelClass}>What are you looking for?{intentRequired ? " *" : ""}</label>
          <select
            name="intent"
            value={formData.intent}
            onChange={handleChange}
            className={`${inputClass} appearance-none pr-10`}
            required={intentRequired}
            data-testid="select-intent"
          >
            <option value="" disabled={intentRequired}>Select one...</option>
            {intentOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="text-red-500 text-xs mb-3" data-testid="form-error">
          {error}
          {submitFailed && (
            <span className="block mt-1 text-[#1A2540]" data-testid="form-error-fallback">
              Please try again, or reach us directly at{" "}
              <a href="tel:7788361962" className="font-medium underline hover:text-[#C9A84C]">778-836-1962</a>
              {" "}or{" "}
              <a href="mailto:chris@maykerfinancial.com" className="font-medium underline hover:text-[#C9A84C]">chris@maykerfinancial.com</a>.
            </span>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-3.5 bg-[#0C1B33] text-white text-sm font-medium tracking-wide rounded hover:bg-[#162647] hover:-translate-y-0.5 transition-all mt-1.5 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="submit-button"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Submitting...
          </>
        ) : (
          "Get My Free Quote →"
        )}
      </button>

      <div className="flex items-center gap-2 mt-3.5 text-[11px] text-[#6B7A99]">
        <Lock className="w-3 h-3" />
        No spam. We respond within 1 business day.
      </div>
    </form>
  );
};

export default LeadForm;
